import dayjs from 'dayjs';
import { mapActions, mapGetters, mapState } from 'vuex';

import { getMenu, getSharedMenu } from '@/api/menu';
import { sendOrder, sendSharedOrder } from '@/api/order';
import {
  CONSUMPTION_LIMIT,
  SNACKBAR_TYPES,
  ENTREE_LIMIT,
  ENTREE_COUNT,
} from '@/utils/constants';
import bus from '@/utils/bus';
import { ERROR_GROUP_BUYING_OVER, ERROR_NOT_IN_GROUP_BUYING } from '@/utils/errorCodeList';
import DisplayedTimeFormat from '../DisplayedTimeFormat/DisplayedTimeFormat.vue';

export default {
  name: 'CartSidebar',
  components: {
    DisplayedTimeFormat,
  },
  props: {
    show: {
      required: true,
      type: Boolean,
    },
  },
  data() {
    return {
      countdown: null,
      dirty: false,
      purchaser: '',
      receivePeriodIndex: 0,
      sent: false,
      touched: false,
    };
  },
  computed: {
    ...mapGetters('cart', [
      'cartItemsCount',
      'cartItems',
      'cartMenuId',
      'cartShareKey',
      'cartTotal',
      'isCartEmpty',
    ]),
    ...mapState('menu', {
      cartMenu: ({ cartMenu }) => cartMenu,
      gettingCartMenu: ({ gettingCartMenu }) => gettingCartMenu,
    }),
    ...mapState('order', {
      sending: ({ sendingOrder }) => sendingOrder,
    }),
    allowedGetMenu() {
      return !!(!this.isCartEmpty && this.cartMenuId);
    },
    allowedReceiveOptions() {
      return this.allowedReceivePeriods.map((period, index) => {
        const {
          end_date: endDate,
          end_time: endTime,
          start_date: startDate,
          start_time: startTime,
        } = period;

        return {
          value: +index,
          text: `${startDate} ${startTime} － ${endDate} ${endTime}`,
        };
      }) || [];
    },
    allowedReceivePeriods() {
      return this.infos?.allowed_receive_periods || [];
    },
    firstPeriod() {
      return this.allowedReceivePeriods?.[0] || {};
    },
    consumptionLimit() {
      return this.infos?.limit;
    },
    consumptionLimitAmount() {
      return this.infos?.limit_amount;
    },
    hasMultipleOnePeriod() {
      return this.allowedReceivePeriods?.length > 1;
    },
    hasOnlyOnePeriod() {
      return this.allowedReceivePeriods?.length === 1;
    },
    hasOverLimit() {
      const limits = {
        [CONSUMPTION_LIMIT.AMOUNT]: this.cartTotal,
        [CONSUMPTION_LIMIT.ITEMS]: this.cartItemsCount,
      };

      return !!this.consumptionLimit && limits[this.consumptionLimit] > this.consumptionLimitAmount;
    },
    hasShareKey() {
      return !!this.cartShareKey;
    },
    infos() {
      return this.cartMenu?.infos || {};
    },
    isInvalid() {
      if (!this.hasShareKey || !this.touched || !this.dirty) {
        return false;
      }
      return !this.purchaser;
    },
    isSendOrderBtnDisabled() {
      return this.gettingCartMenu
        || this.isCartEmpty
        || this.hasOverLimit
        || this.hasOverEntreeAvailable
        || this.isInvalid
        || this.sending;
    },
    items() {
      return this.cartItems?.map(({ selected_items: selected = [], ...payload }) => ({
        ...payload,
        selected,
      })) || [];
    },
    limitText() {
      const amount = this.$options.filters.commaFormat(this.infos?.limit_amount);
      const limits = {
        [CONSUMPTION_LIMIT.AMOUNT]: `請移除部分項目，以下選購品項的訂單累計金額 ${this.$options.filters.priceFormat(this.cartTotal)}，已達每人限制總消費金額 ${this.$options.filters.priceFormat(amount)} 上限`,
        [CONSUMPTION_LIMIT.ITEMS]: `每人限制總訂購項目數 ${amount} 項，請移除部分項目`,
      };
      return limits[this.consumptionLimit];
    },
    purchaserMsg() {
      return !this.purchaser && this.dirty ? '訪客姓名為必填' : '';
    },
    receiveTime() {
      return this.allowedReceivePeriods?.[this.receivePeriodIndex || 0] || {};
    },
    hasOverEntreeAvailable() {
      return this.items.some(({ index }) => this.isOverEntreeAvailable(index));
    },
  },
  watch: {
    purchaser(newValue) {
      if (newValue && !this.dirty) {
        this.dirty = true;
      }
    },
    async show(newVal) {
      if (newVal) {
        window.addEventListener('click', (e) => this.clickInTarget(e));
        this.loadCartMenu();
      } else {
        window.removeEventListener('click', (e) => this.clickInTarget(e));
      }
    },
  },
  methods: {
    ...mapActions('cart', {
      clearCart: 'CLEAR_CART_ITEMS',
      modifyCartItem: 'MODIFY_CART_ITEM',
      removeCartItem: 'REMOVE_CART_ITEM',
    }),
    ...mapActions('menu', {
      getCartMenu: 'GET_CART_MENU',
    }),
    ...mapActions('order', {
      sendOrder: 'SEND_ORDER',
    }),
    onTouched() {
      this.touched = true;
    },
    async send() {
      if (this.hasShareKey && !this.purchaser) {
        this.dirty = true;
        this.touched = true;
        return;
      }

      try {
        const receiveTimeId = this.receiveTime?.id;
        const data = {
          cb: sendOrder,
          receive_time_id: receiveTimeId,
        };
        if (this.hasShareKey) {
          data.cb = sendSharedOrder;
          data.name = this.purchaser;
          data.share_key = this.cartShareKey;
        }
        await this.sendOrder(data);
        this.sent = true;
        if (this.hasShareKey) {
          this.$router.push({
            name: 'OrderCompleted',
            params: {
              infos: {
                ...this.infos,
                created_at: dayjs().format('YYYY/MM/DD HH:mm'),
                period: this.receiveTime,
                purchaser: data.name,
                total: this.cartTotal,
              },
              items: this.cartItems,
            },
          });
        }
        await this.clearCart();
        this.startCountdown();
        bus.$emit('REFRESH_MENU');
      } catch (error) {
        const { message: msg = '', status } = error;
        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          msg,
        });
        if (status === ERROR_GROUP_BUYING_OVER || status === ERROR_NOT_IN_GROUP_BUYING) {
          this.clearCart();
          this.$router.replace('/menu');
          return;
        }
        this.loadCartMenu();
        bus.$emit('REFRESH_MENU');
      }
    },
    clickInTarget(e) {
      const ids = [
        'cart-sidebar',
        'open-cart',
        'mobile-show-cart-btn',
      ];

      if (!ids.some((id) => document.getElementById(id)?.contains(e.target))) {
        this.close();
      }
    },
    close() {
      this.stopCountdown();
      this.$emit('close');
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    changeCount($event, itemIndex) {
      const count = +$event?.target?.value;
      if (!count) {
        this.removeCartItem(itemIndex);
      } else {
        this.modifyCartItem({
          itemIndex,
          ...{ count },
        });
      }
    },
    isOverEntreeAvailable(entreeIndex) {
      const available = this.getEntreeAvailableCount(entreeIndex);
      const totalCount = this.getEntreeOrderedCount(entreeIndex);
      return totalCount > available;
    },
    getEntreeAvailableCount(entreeIndex) {
      const { entree_limit: entreeLimit = {} } = this.cartMenu || {};
      const { available = ENTREE_LIMIT.MAXIMUM } = entreeLimit[entreeIndex] || {};
      return available;
    },
    getEntreeOrderedCount(entreeIndex) {
      return this.items
        .filter(({ index }) => index === entreeIndex)
        .reduce((sum, { count }) => sum + count, 0);
    },
    getCartItemCountOptions(entreeIndex, itemCount) {
      const options = [
        {
          text: '移除',
          value: 0,
        },
      ];

      let max = ENTREE_COUNT.MAXIMUM;
      const min = ENTREE_COUNT.MINIMUM;
      const { entree_limit: entreeLimit = {} } = this.cartMenu || {};
      if (entreeLimit[entreeIndex]) {
        const orderedCount = this.getEntreeOrderedCount(entreeIndex);
        const available = this.getEntreeAvailableCount(entreeIndex);
        const remaining = Math.max(available - orderedCount, 0);
        max = Math.min(itemCount + remaining, ENTREE_LIMIT.MAXIMUM);
      }

      for (let index = min; index <= max; index += 1) {
        options.push({
          text: index,
          value: index,
        });
      }

      return options;
    },
    gotoMenu() {
      this.$router.push('/menu');
      this.close();
    },
    async loadCartMenu() {
      if (!this.allowedGetMenu) {
        return;
      }
      const payload = this.hasShareKey ? {
        cb: getSharedMenu,
        key: this.cartShareKey,
      } : {
        cb: getMenu,
        key: this.cartMenuId,
      };
      await this.getCartMenu(payload);
    },
    startCountdown() {
      this.stopCountdown();
      this.countdown = setTimeout(() => {
        this.gotoMenu();
      }, 3000);
    },
    stopCountdown() {
      if (this.countdown) {
        clearTimeout(this.countdown);
      }
    },
  },
  beforeDestroy() {
    this.stopCountdown();
  },
};
